import { useMemo } from 'react';
import { deleteGameEventById } from 'services/api/gameEventsApi';
import { useHistory } from 'react-router-dom';
import { strings } from 'strings';
import { GameEvent } from 'types/components/GameEvent';

import { Box, FlexContainer, Text } from '../../../../shared/Typhography';
import { TableComponent } from '../../../../shared/Components/Table/Table';
import { Button, Select, Splash } from '../../../../shared/Components';
import DatePickerInput from '../../../../shared/Components/DatePickerInput';
import { MODAL_TYPES, useGlobalModalContext } from '../../../../context/GlobalModal';

import useGameEvents from './hooks/useGameEvents';
import { isAbleToMutateEvent } from './utils/helpers';
import { STATUS_FILTERS_OPTIONS } from './contants';

const GameEventsScreen = () => {
  const { data, isLoading, toggleDateSort, fetch, resetFilters, addFilter, filters } =
    useGameEvents();

  const { showModal } = useGlobalModalContext();

  const history = useHistory();

  const dotRangeDays = useMemo(
    () =>
      data?.length
        ? data.map((item) => ({ startDate: item.startDate, endDate: item.endDate }))
        : [],
    [data],
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Event start - end date',
        accessor: 'startEndDate',
        Cell: (cell: any) => (
          <div style={{ marginLeft: 10 }}>
            <div>
              {cell?.row?.original?.startDate?.toLocaleString('en-us', {
                month: 'short',
                year: 'numeric',
                day: 'numeric',
              }) || '/'}
            </div>
            <div>
              {cell?.row?.original?.endDate?.toLocaleString('en-us', {
                month: 'short',
                year: 'numeric',
                day: 'numeric',
              }) || '/'}
            </div>
          </div>
        ),
        width: '10%',
        disableSortBy: false,
        onHeaderClick: () => toggleDateSort(),
      },
      {
        Header: 'Event ID',
        accessor: 'id',
        Cell: (cell: any) => <div>{cell?.row?.original?.id}</div>,
        disableSortBy: true,
        width: '7%',
      },
      {
        Header: 'Event Image',
        accessor: 'image',
        Cell: (cell: any) => <img width="100" height="80" src={cell?.row?.original?.image} />,
        disableSortBy: true,
        width: '5%',
      },
      {
        Header: 'Event Name',
        accessor: 'name',
        Cell: (cell: any) => (
          <>
            {!cell?.row?.original?.published && strings.gameEvent.list.draft}{' '}
            {cell?.row?.original?.name}
          </>
        ),
        disableSortBy: true,
        width: '10%',
      },
      {
        Header: 'Entry fee',
        accessor: 'entryFee',
        Cell: (cell: any) => {
          const entryFee = cell?.row?.original?.entryFee;
          let value = '';
          if (entryFee) {
            const entryFeeKeys = Object.keys(entryFee);
            if (entryFeeKeys.length) {
              const currency = entryFeeKeys[0];
              const amount = entryFee[currency];
              value = `${amount} ${currency}`;
            }
          }
          return <>{value}</>;
        },
        disableSortBy: true,
        width: '5%',
      },
      {
        Header: 'Levels',
        accessor: 'eventLevelsCount',
        Cell: (cell: any) => <>{cell?.row?.original?.eventLevelsCount}</>,
        disableSortBy: true,
        width: '3%',
      },
      {
        Header: 'Difficulty',
        accessor: 'difficulty',
        Cell: (cell: any) => <>{cell?.row?.original?.difficulty}</>,
        disableSortBy: true,
        width: '3%',
      },
      {
        Header: '',
        accessor: 'actions',
        Cell: (cell: any) => (
          <>
            <Button
              variant="link"
              onClick={() => {
                history.push(`/edit-game-event/${cell?.row?.original?.id}`);
              }}
              disabled={!isAbleToMutateEvent(cell?.row?.original as GameEvent)}
            >
              Edit
            </Button>
            <Button
              variant="link"
              onClick={() => {
                showModal(MODAL_TYPES.DIALOG_MODAL, {
                  title: 'Delete Event?',
                  text: `Are you sure you want to delete this event?`,
                  cancelButtonText: 'Cancel',
                  submitButtonText: 'Delete',
                  onSubmit: async () => {
                    await deleteGameEventById(cell?.row?.original?.id);
                    fetch();
                  },
                });
              }}
              disabled={!isAbleToMutateEvent(cell?.row?.original as GameEvent)}
            >
              Delete
            </Button>
          </>
        ),
        disableSortBy: true,
        width: '3%',
      },
    ],
    [toggleDateSort],
  );

  return (
    <>
      <Box margin="150px auto 0">
        <FlexContainer justifyContent="space-between">
          <DatePickerInput
            selected={null}
            onChange={() => {}}
            inline={true}
            label="Date Range"
            dotRangeDays={dotRangeDays}
            readOnlyCalendar={true}
          />
          <FlexContainer
            flexDirection="column"
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <Button height={60} width={150} onClick={() => history.push('/create-game-events')}>
              <span style={{ fontSize: '16px' }}>Create</span>
            </Button>
            <Select
              id="eventsFilterPublished"
              label="All Events / Published / Drafts"
              onChange={(e) => {
                const value = e?.value;
                resetFilters();
                if (typeof value === 'undefined') return;
                addFilter({
                  field: 'published',
                  operator: '==',
                  value,
                });
              }}
              options={STATUS_FILTERS_OPTIONS}
              value={
                filters.find((item) => item?.field === 'published')
                  ? filters.find((item) => item.field === 'published')?.value === true
                    ? STATUS_FILTERS_OPTIONS[0]
                    : STATUS_FILTERS_OPTIONS[1]
                  : null
              }
              isClearable={true}
              style={{ width: '250px' }}
            />
          </FlexContainer>
        </FlexContainer>
      </Box>
      <Box margin="50px auto 150px">
        {isLoading ? (
          <Splash margin="40px 0 0 0" position="relative" />
        ) : data?.length === 0 ? (
          <Text textAlign="center">No results...</Text>
        ) : (
          <TableComponent
            loadingMore={false}
            fetchMore={false}
            columns={columns}
            data={data.map((item, index) => ({ index: index + 1, ...item }))}
          />
        )}
      </Box>
    </>
  );
};

export default GameEventsScreen;
